import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import ScreenHeader from '@/components/ui/ScreenHeader';
import AppScreen from '@/components/ui/AppScreen';
import { useAlturaElemento } from '@/hooks/useAlturaElemento';
import EstadoVazio from '@/components/ui/EstadoVazio';
import BolhaMensagem from './components/BolhaMensagem';
import Composer from './components/Composer';
import FolhaMemoria from './components/FolhaMemoria';
import CartaoHoje from './components/CartaoHoje';
import ChipsPerguntas from './components/ChipsPerguntas';
import { useResumoAgente } from './useResumoAgente';
import { ErroAssistente, chamarAssistente } from './utils';
import type {
  EnviarResposta,
  HistoricoResposta,
  MemoriaItem,
  MensagemAssistente,
  UsoAssistente,
} from './types';

type EstadoAcesso = 'carregando' | 'permitido' | 'negado' | 'nao-encontrado';

export default function AssistenteDirecao() {
  const navigate = useNavigate();
  const { eventoId } = useParams();
  const { user, pronto } = useAuth();
  const eventoIdNum = Number(eventoId);

  const [acesso, setAcesso] = useState<EstadoAcesso>('carregando');
  const [erroAcesso, setErroAcesso] = useState<string | null>(null);
  const [eventoNome, setEventoNome] = useState('');

  const [carregandoHistorico, setCarregandoHistorico] = useState(true);
  const [erroHistorico, setErroHistorico] = useState<string | null>(null);
  const [ligado, setLigado] = useState(true);
  const [mensagens, setMensagens] = useState<MensagemAssistente[]>([]);
  const [memoria, setMemoria] = useState<MemoriaItem[]>([]);
  const [uso, setUso] = useState<UsoAssistente | null>(null);
  const [temMais, setTemMais] = useState(false);
  const [carregandoMais, setCarregandoMais] = useState(false);

  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [limiteAtingido, setLimiteAtingido] = useState(false);
  const [sheetMemoria, setSheetMemoria] = useState(false);

  const alturaAntesRef = useRef(0);
  // Mensagens que já estavam na tela quando a conversa abriu: essas NÃO animam.
  const idsIniciais = useRef<Set<string | number> | null>(null);

  // Alturas reais do rodapé fixo (composer + linha de chips): servem para
  // reservar o espaço certo embaixo da conversa, sem esconder a última mensagem
  // nem o "Carregar mais antigas".
  const { ref: composerRef, altura: alturaComposer } = useAlturaElemento();
  const { ref: chipsRef, altura: alturaChips } = useAlturaElemento();
  // O rodapé inteiro (chips + campo + respiro de baixo) também é medido, para
  // a reserva nunca ficar menor que ele.
  const { ref: rodapeRef, altura: alturaRodape } = useAlturaElemento();
  const alturaComposerOk = alturaComposer > 0 ? alturaComposer : 64;
  const reservaRodape = Math.max(alturaRodape, alturaComposerOk + alturaChips) + 16;

  // Resumo do dia: uma chamada por abertura de tela, em paralelo com o histórico.
  const { dados: resumo, estado: estadoResumo } = useResumoAgente(
    eventoIdNum,
    acesso === 'permitido',
  );

  // ——— Acesso: só quem é dono/direção abre, igual ao painel. ———
  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      navigate(`/entrar?voltar=${encodeURIComponent(`/gerenciar/${eventoId}/assistente`)}`, {
        replace: true,
      });
      return;
    }
    if (Number.isNaN(eventoIdNum)) {
      setAcesso('nao-encontrado');
      return;
    }
    let ativo = true;
    (async () => {
      try {
        const { data: pode, error: errPode } = await supabase.rpc('pode_editar_evento', {
          p_evento: eventoIdNum,
        });
        if (errPode) throw errPode;
        if (!pode) {
          if (ativo) setAcesso('negado');
          return;
        }
        const { data: ev, error: errEv } = await supabase
          .from('eventos')
          .select('id, nome')
          .eq('id', eventoIdNum)
          .maybeSingle();
        if (errEv) throw errEv;
        if (!ev) {
          if (ativo) setAcesso('nao-encontrado');
          return;
        }
        if (ativo) {
          setEventoNome((ev as { nome: string }).nome);
          setAcesso('permitido');
        }
      } catch (e) {
        if (ativo) {
          setErroAcesso(mensagemAmigavel(e, 'Não foi possível abrir o assistente.'));
          setAcesso('nao-encontrado');
        }
      }
    })();
    return () => {
      ativo = false;
    };
  }, [pronto, user, eventoId, eventoIdNum, navigate]);

  useEffect(() => {
    if (idsIniciais.current === null && !carregandoHistorico && mensagens.length > 0) {
      idsIniciais.current = new Set(mensagens.map((m) => m.id));
    }
  }, [carregandoHistorico, mensagens]);

  // ——— Histórico da conversa. ———
  const carregarHistorico = useCallback(async () => {
    setCarregandoHistorico(true);
    setErroHistorico(null);
    try {
      const resp = await chamarAssistente<HistoricoResposta>({
        acao: 'historico',
        evento_id: eventoIdNum,
      });
      setMensagens(
        (resp.mensagens || []).map((m) => ({
          id: m.id,
          papel: m.papel,
          texto: m.texto,
          criado_em: m.criado_em,
        })),
      );
      setMemoria(resp.memoria || []);
      setUso(resp.uso || null);
      setLigado(resp.ligado !== false);
      setTemMais((resp.mensagens || []).length >= 60);
    } catch (e) {
      setErroHistorico(e instanceof Error ? e.message : 'Não foi possível abrir o assistente agora.');
    } finally {
      setCarregandoHistorico(false);
    }
  }, [eventoIdNum]);

  useEffect(() => {
    if (acesso === 'permitido') carregarHistorico();
  }, [acesso, carregarHistorico]);

  // Auto-rola para o fim ao abrir, depois de cada mudança na conversa e depois
  // que o card "Hoje" termina de carregar (ele nasce acima da lista).
  useEffect(() => {
    if (carregandoHistorico || carregandoMais) return;
    const rolar = () =>
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' });
    rolar();
    // O card "Hoje" pode crescer depois do primeiro desenho: rola de novo.
    const t = window.setTimeout(rolar, 120);
    return () => window.clearTimeout(t);
  }, [carregandoHistorico, carregandoMais, mensagens, estadoResumo, resumo]);

  async function carregarMaisAntigas() {
    const primeira = mensagens[0];
    if (!primeira || carregandoMais) return;
    setCarregandoMais(true);
    alturaAntesRef.current = document.documentElement.scrollHeight;
    const rolagemAntes = window.scrollY;
    try {
      const resp = await chamarAssistente<HistoricoResposta>({
        acao: 'historico',
        evento_id: eventoIdNum,
        antes_de: primeira.id,
      });
      const novas = (resp.mensagens || []).map((m) => ({
        id: m.id,
        papel: m.papel,
        texto: m.texto,
        criado_em: m.criado_em,
      }));
      setMensagens((prev) => [...novas, ...prev]);
      setTemMais(novas.length >= 60);
      requestAnimationFrame(() => {
        const cresceu = document.documentElement.scrollHeight - alturaAntesRef.current;
        window.scrollTo({ top: rolagemAntes + cresceu });
      });
    } catch (e) {
      // Falha silenciosa aqui: a pessoa pode tocar "Carregar mais antigas" de novo.
      void e;
    } finally {
      setCarregandoMais(false);
    }
  }

  async function enviar(mensagemTexto: string) {
    const valor = mensagemTexto.trim();
    if (!valor || enviando) return;
    setTexto('');
    setEnviando(true);

    const idTemp = `tmp-${Date.now()}`;
    const agora = new Date().toISOString();
    setMensagens((prev) => [
      ...prev,
      { id: idTemp, papel: 'usuario', texto: valor, criado_em: agora },
      { id: `${idTemp}-pensando`, papel: 'assistente', texto: '', criado_em: agora, pendente: true },
    ]);

    try {
      const resp = await chamarAssistente<EnviarResposta>({
        acao: 'enviar',
        evento_id: eventoIdNum,
        mensagem: valor,
      });
      setMensagens((prev) =>
        prev.map((m) =>
          m.id === `${idTemp}-pensando`
            ? {
                id: resp.mensagem_id,
                papel: 'assistente',
                texto: resp.resposta,
                criado_em: new Date().toISOString(),
              }
            : m,
        ),
      );
      setUso(resp.uso || null);
    } catch (e) {
      const erro = e instanceof ErroAssistente ? e : null;
      const mensagemErro = e instanceof Error ? e.message : 'Não foi possível enviar. Tente de novo.';
      setMensagens((prev) =>
        prev
          .filter((m) => m.id !== `${idTemp}-pensando`)
          .map((m) => (m.id === idTemp ? { ...m, erro: mensagemErro } : m)),
      );
      if (erro?.limite) {
        setLimiteAtingido(true);
      }
    } finally {
      setEnviando(false);
    }
  }

  function reenviar(idUsuario: number | string, texto: string) {
    setMensagens((prev) => prev.filter((m) => m.id !== idUsuario));
    enviar(texto);
  }

  const usoRotulo = useMemo(() => {
    if (!uso) return null;
    return `${uso.usadas} de ${uso.limite} neste mês`;
  }, [uso]);

  if (acesso === 'carregando') {
    return (
      <AppScreen>
        <div className="mx-auto w-full max-w-[720px] px-4 py-8">
          <div className="h-14 esqueleto rounded-[18px] bg-background-200/70" />
          <div className="mt-4 h-44 esqueleto rounded-[16px] bg-background-200/70" />
          <div className="mt-4 h-64 esqueleto rounded-[18px] bg-background-200/70" />
        </div>
      </AppScreen>
    );
  }

  if (acesso === 'negado') {
    return (
      <AppScreen>
        <div className="flex min-h-screen items-center justify-center px-5 py-8">
          <div className="w-full max-w-[420px] rounded-[22px] bg-background-50 p-8 text-center shadow-pcard">
            <h1 className="text-[20px] font-extrabold text-foreground-950">
              Você não está na direção deste evento
            </h1>
            <p className="mt-2 text-[15px] text-foreground-600">
              Só quem organiza ou faz parte da direção abre o assistente.
            </p>
            <Link
              to={`/gerenciar/${eventoId}`}
              className="mt-6 flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-[18px] bg-primary-500 px-6 py-3.5 text-[15px] font-bold text-white transition-colors hover:bg-primary-600"
            >
              Voltar ao painel
            </Link>
          </div>
        </div>
      </AppScreen>
    );
  }

  if (acesso === 'nao-encontrado') {
    return (
      <AppScreen>
        <div className="flex min-h-screen items-center justify-center px-5 py-8">
          <div className="w-full max-w-[420px] rounded-[22px] bg-background-50 p-8 text-center shadow-pcard">
            <h1 className="text-[20px] font-extrabold text-foreground-950">
              {erroAcesso ? 'Não foi possível carregar' : 'Evento não encontrado'}
            </h1>
            <p className="mt-2 text-[15px] text-foreground-600">
              {erroAcesso ?? 'Este evento não existe ou não está mais disponível.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/meus-eventos')}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 px-6 py-3.5 text-[15px] font-bold text-white transition-colors hover:bg-primary-600"
            >
              Meus eventos
            </button>
          </div>
        </div>
      </AppScreen>
    );
  }

  const conversaVazia = !carregandoHistorico && !erroHistorico && ligado && mensagens.length === 0;
  const mostrarHoje = estadoResumo === 'carregando' || (estadoResumo === 'pronto' && resumo);

  return (
    <AppScreen>
      <ScreenHeader
        titulo="Seu agente"
        subtitulo={eventoNome}
        voltar
        acao={
          <div className="flex items-center gap-2">
            {usoRotulo && (
              <span className="hidden whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-[12px] font-semibold text-foreground-600 sm:inline-flex">
                {usoRotulo}
              </span>
            )}
            <button
              type="button"
              onClick={() => setSheetMemoria(true)}
              aria-label="O que ele lembra"
              className="aperta flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-background-100 text-[12px] font-semibold text-foreground-700 transition-colors hover:bg-background-200/70 min-[420px]:h-9 min-[420px]:w-auto min-[420px]:px-3"
            >
              <i className="ri-brain-line" aria-hidden />
              <span className="hidden min-[420px]:inline">O que ele lembra</span>
            </button>
          </div>
        }
      />

      {usoRotulo && (
        <div className="mx-auto w-full max-w-[720px] px-4 pt-2 sm:hidden">
          <span className="inline-flex whitespace-nowrap rounded-full bg-background-100 px-3 py-1 text-[12px] font-semibold text-foreground-600">
            {usoRotulo}
          </span>
        </div>
      )}

      {/* Resumo do dia: some sozinho se a função não responder */}
      {mostrarHoje && (
        <div className="mx-auto w-full max-w-[720px] px-4 pt-3">
          <CartaoHoje
            eventoId={eventoIdNum}
            dados={resumo}
            carregando={estadoResumo === 'carregando'}
          />
        </div>
      )}

      {/* Conversa */}
      <main
        className="mx-auto w-full max-w-[720px] px-4 pt-3"
        style={{ paddingBottom: reservaRodape }}
      >
        {carregandoHistorico && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`h-16 esqueleto rounded-[18px] bg-background-200/60 ${
                  i % 2 === 0 ? 'ml-auto w-2/3' : 'w-3/4'
                }`}
              />
            ))}
          </div>
        )}

        {!carregandoHistorico && erroHistorico && (
          <EstadoVazio
            icone="ri-robot-2-line"
            titulo="Não foi possível abrir o agente agora."
            descricao={erroHistorico}
            acaoRotulo="Tentar de novo"
            aoClicarAcao={carregarHistorico}
          />
        )}

        {!carregandoHistorico && !erroHistorico && !ligado && (
          <EstadoVazio
            icone="ri-sparkling-2-line"
            titulo="O agente ainda não foi ligado para este evento."
          />
        )}

        {conversaVazia && (
          <div className="px-4 pb-4 pt-10 text-center">
            <p className="mx-auto max-w-[420px] text-[15px] leading-relaxed text-foreground-600">
              Sou o seu agente neste evento. Cuido das vendas e do dinheiro, acompanho todo dia e dou
              ideias para vender mais. Pergunte ou toque numa sugestão.
            </p>
          </div>
        )}

        {!carregandoHistorico && !erroHistorico && ligado && mensagens.length > 0 && (
          <>
            {temMais && (
              <div className="mb-3 text-center">
                <button
                  type="button"
                  onClick={carregarMaisAntigas}
                  disabled={carregandoMais}
                  className="aperta cursor-pointer whitespace-nowrap text-[13px] font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-50"
                >
                  {carregandoMais ? 'Carregando…' : 'Carregar mais antigas'}
                </button>
              </div>
            )}

            <div className="space-y-3">
              {mensagens.map((m) => {
                const animar = idsIniciais.current ? !idsIniciais.current.has(m.id) : false;
                return (
                  <motion.div
                    key={m.id}
                    initial={animar ? { opacity: 0, y: 10 } : false}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <BolhaMensagem
                      mensagem={m}
                      aoTentarDeNovo={
                        m.erro && m.papel === 'usuario' ? () => reenviar(m.id, m.texto) : undefined
                      }
                    />
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </main>

      {/* Composer + perguntas prontas */}
      {!carregandoHistorico && !erroHistorico && ligado && (
        <div
          ref={rodapeRef}
          className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <div ref={chipsRef}>
            <ChipsPerguntas
              aoEscolher={(c) => enviar(c)}
              escondido={enviando}
              desabilitado={enviando || limiteAtingido}
            />
          </div>
          <div ref={composerRef}>
            <Composer
              valor={texto}
              aoMudar={setTexto}
              aoEnviar={() => enviar(texto)}
              desabilitado={enviando || limiteAtingido}
              mensagemDesabilitado={
                limiteAtingido
                  ? 'O limite de mensagens deste mês para este evento acabou.'
                  : null
              }
              mostrarBotaoChips={false}
              aoEscolherChip={(c) => enviar(c)}
              placeholder="Pergunte sobre vendas, dinheiro, pessoas…"
            />
          </div>
        </div>
      )}

      <FolhaMemoria aberto={sheetMemoria} aoFechar={() => setSheetMemoria(false)} memoria={memoria} />
    </AppScreen>
  );
}